import type { BotDifficulty } from '@signalclash/shared';

import type { AudioBus } from '../audio/AudioBus.js';
import type { SettingsStore } from '../save/Settings.js';
import {
  SCROLL_SPEED_MAX_PX_PER_SEC,
  SCROLL_SPEED_MIN_PX_PER_SEC,
} from '../save/Settings.js';

const PANEL_ID = 'settings-panel';
const STYLE_ID = 'settings-panel-styles';

const BOT_DIFFICULTIES: BotDifficulty[] = ['recruit', 'tactical', 'commander'];

const BOT_DIFFICULTY_LABELS: Record<BotDifficulty, string> = {
  recruit: 'Recruit',
  tactical: 'Tactical',
  commander: 'Commander',
};

const BOT_DIFFICULTY_BLURBS: Record<BotDifficulty, string> = {
  recruit: 'New to the game · capture-only · no abilities',
  tactical: 'V1 default · full roster · uses abilities',
  commander: 'V1.5 top tier · 250 ms reaction · matchup-aware doctrines',
};

interface SettingsPanelOptions {
  audio: AudioBus;
  settings: SettingsStore;
  // Notified when the bot-difficulty selection changes so ClientApp can pipe
  // the choice into its currentBotConfig before the next match starts.
  onBotDifficultyChange?: (d: BotDifficulty) => void;
  onClose: () => void;
}

// V1 Settings panel: a DOM overlay with sliders for master / SFX volume,
// camera scroll speed, and (V1.5) a Bot difficulty segmented control. Audio
// persistence is owned by AudioBus; SettingsStore owns scroll speed and bot
// difficulty. This panel is a thin presentation layer that reads current
// values on show() and writes them on slider/button input.
export class SettingsPanel {
  private readonly opts: SettingsPanelOptions;
  private root: HTMLDivElement | null = null;
  private masterRange: HTMLInputElement | null = null;
  private sfxRange: HTMLInputElement | null = null;
  private musicRange: HTMLInputElement | null = null;
  private voiceRange: HTMLInputElement | null = null;
  private scrollRange: HTMLInputElement | null = null;
  private masterValueEl: HTMLSpanElement | null = null;
  private sfxValueEl: HTMLSpanElement | null = null;
  private musicValueEl: HTMLSpanElement | null = null;
  private voiceValueEl: HTMLSpanElement | null = null;
  private scrollValueEl: HTMLSpanElement | null = null;
  private analyticsCheckbox: HTMLInputElement | null = null;
  private difficultyBtns = new Map<BotDifficulty, HTMLButtonElement>();
  private difficultyHandlers = new Map<BotDifficulty, () => void>();
  private difficultyBlurbEl: HTMLDivElement | null = null;
  private closeBtn: HTMLButtonElement | null = null;
  private shown = false;

  constructor(opts: SettingsPanelOptions) {
    this.opts = opts;
    injectStyles();
    this.mount();
    this.hide();
  }

  show(): void {
    if (this.root === null) return;
    this.shown = true;
    this.refresh();
    this.root.style.display = 'flex';
    this.closeBtn?.focus();
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
    this.shown = false;
  }

  isVisible(): boolean {
    return this.shown;
  }

  private mount(): void {
    this.root = ensureRoot();
    this.root.innerHTML = '';

    const card = document.createElement('div');
    card.className = 'settings__card';

    const title = document.createElement('h2');
    title.className = 'settings__title';
    title.textContent = 'SETTINGS';
    card.appendChild(title);

    card.appendChild(this.buildAudioSection());
    card.appendChild(this.buildCameraSection());
    card.appendChild(this.buildBotDifficultySection());
    card.appendChild(this.buildAnalyticsSection());

    this.closeBtn = document.createElement('button');
    this.closeBtn.type = 'button';
    this.closeBtn.className = 'settings__btn settings__btn--primary';
    this.closeBtn.textContent = 'Done';
    this.closeBtn.addEventListener('click', () => {
      this.hide();
      this.opts.onClose();
    });
    card.appendChild(this.closeBtn);

    this.root.appendChild(card);
  }

  private buildAudioSection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'settings__section';
    const heading = document.createElement('div');
    heading.className = 'settings__section-heading';
    heading.textContent = 'Audio';
    section.appendChild(heading);

    const masterRow = this.buildSliderRow({
      label: 'Master',
      min: 0,
      max: 1,
      step: 0.01,
      get: () => this.opts.audio.getMasterVolume(),
      set: (v) => this.opts.audio.setMasterVolume(v),
      format: (v) => `${Math.round(v * 100)}%`,
    });
    this.masterRange = masterRow.range;
    this.masterValueEl = masterRow.valueEl;
    section.appendChild(masterRow.row);

    const sfxRow = this.buildSliderRow({
      label: 'SFX',
      min: 0,
      max: 1,
      step: 0.01,
      get: () => this.opts.audio.getSfxVolume(),
      set: (v) => this.opts.audio.setSfxVolume(v),
      format: (v) => `${Math.round(v * 100)}%`,
    });
    this.sfxRange = sfxRow.range;
    this.sfxValueEl = sfxRow.valueEl;
    section.appendChild(sfxRow.row);

    const musicRow = this.buildSliderRow({
      label: 'Music',
      min: 0,
      max: 1,
      step: 0.01,
      get: () => this.opts.audio.getMusicVolume(),
      set: (v) => this.opts.audio.setMusicVolume(v),
      format: (v) => `${Math.round(v * 100)}%`,
    });
    this.musicRange = musicRow.range;
    this.musicValueEl = musicRow.valueEl;
    section.appendChild(musicRow.row);

    const voiceRow = this.buildSliderRow({
      label: 'Voice',
      min: 0,
      max: 1,
      step: 0.01,
      get: () => this.opts.audio.getVoiceVolume(),
      set: (v) => this.opts.audio.setVoiceVolume(v),
      format: (v) => `${Math.round(v * 100)}%`,
    });
    this.voiceRange = voiceRow.range;
    this.voiceValueEl = voiceRow.valueEl;
    section.appendChild(voiceRow.row);

    return section;
  }

  private buildCameraSection(): HTMLElement {
    const section = document.createElement('section');
    // On coarse-pointer (touch) devices, keyboard-scroll-speed is irrelevant —
    // camera movement is via single-finger drag or two-finger pinch/pan. Hide
    // the whole Camera section on mobile so the settings panel doesn't surface
    // a meaningless slider.
    section.className = 'settings__section settings__section--camera';
    const heading = document.createElement('div');
    heading.className = 'settings__section-heading';
    heading.textContent = 'Camera';
    section.appendChild(heading);

    const scrollRow = this.buildSliderRow({
      label: 'Scroll speed',
      min: SCROLL_SPEED_MIN_PX_PER_SEC,
      max: SCROLL_SPEED_MAX_PX_PER_SEC,
      step: 50,
      get: () => this.opts.settings.get().scrollSpeedPxPerSec,
      set: (v) => this.opts.settings.set({ scrollSpeedPxPerSec: v }),
      format: (v) => `${Math.round(v)} px/s`,
    });
    this.scrollRange = scrollRow.range;
    this.scrollValueEl = scrollRow.valueEl;
    section.appendChild(scrollRow.row);

    const help = document.createElement('div');
    help.className = 'settings__help';
    help.textContent = 'Hold arrow keys to pan. Speed scales with zoom level.';
    section.appendChild(help);

    return section;
  }

  // V1.5 segmented control: Recruit / Tactical / Commander. The choice is
  // persisted in SettingsStore (so it survives reload), and applied to the
  // next match via the onBotDifficultyChange hook — ClientApp.setBotDifficulty
  // already rebuilds the BotCommandSource at match start.
  private buildBotDifficultySection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'settings__section';
    const heading = document.createElement('div');
    heading.className = 'settings__section-heading';
    heading.textContent = 'Bot difficulty';
    section.appendChild(heading);

    const group = document.createElement('div');
    group.className = 'settings__diff-group';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Bot difficulty');
    for (const d of BOT_DIFFICULTIES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'settings__diff-btn';
      btn.textContent = BOT_DIFFICULTY_LABELS[d];
      btn.setAttribute('role', 'radio');
      const handler = (): void => {
        this.opts.settings.set({ botDifficulty: d });
        this.opts.onBotDifficultyChange?.(d);
        this.applySelectedDifficulty(d);
      };
      btn.addEventListener('click', handler);
      this.difficultyBtns.set(d, btn);
      this.difficultyHandlers.set(d, handler);
      group.appendChild(btn);
    }
    section.appendChild(group);

    this.difficultyBlurbEl = document.createElement('div');
    this.difficultyBlurbEl.className = 'settings__diff-blurb';
    section.appendChild(this.difficultyBlurbEl);

    this.applySelectedDifficulty(this.opts.settings.get().botDifficulty);

    return section;
  }

  // V4 portal requirement: anonymous analytics opt-out toggle. Network-mode
  // matches fire a single anonymous `match_started` beacon (no PII, no IP).
  // This toggle lets players opt out if they prefer. Local (vs-bot) play never
  // sends analytics regardless.
  private buildAnalyticsSection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'settings__section';
    const heading = document.createElement('div');
    heading.className = 'settings__section-heading';
    heading.textContent = 'Privacy';
    section.appendChild(heading);

    const row = document.createElement('div');
    row.className = 'settings__check-row';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = 'settings-analytics-enabled';
    checkbox.className = 'settings__checkbox';
    checkbox.checked = this.opts.settings.get().analyticsEnabled;
    checkbox.addEventListener('change', () => {
      this.opts.settings.set({ analyticsEnabled: checkbox.checked });
    });
    this.analyticsCheckbox = checkbox;

    const label = document.createElement('label');
    label.htmlFor = 'settings-analytics-enabled';
    label.className = 'settings__check-label';
    label.textContent = 'Anonymous match analytics (no personal data)';

    row.appendChild(checkbox);
    row.appendChild(label);
    section.appendChild(row);

    const help = document.createElement('div');
    help.className = 'settings__help';
    help.textContent = 'Only applies to online matches. Never includes your name, IP, or device info.';
    section.appendChild(help);

    return section;
  }

  private applySelectedDifficulty(active: BotDifficulty): void {
    for (const [d, btn] of this.difficultyBtns) {
      const selected = d === active;
      btn.classList.toggle('settings__diff-btn--selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    if (this.difficultyBlurbEl !== null) {
      this.difficultyBlurbEl.textContent = BOT_DIFFICULTY_BLURBS[active];
    }
  }

  // Builds a slider row with live min/max/step + label + value readout. Returns
  // the row element plus refs to the input and value span so refresh() can
  // resync them when show() reopens the panel.
  private buildSliderRow(args: {
    label: string;
    min: number;
    max: number;
    step: number;
    get: () => number;
    set: (v: number) => void;
    format: (v: number) => string;
  }): { row: HTMLElement; range: HTMLInputElement; valueEl: HTMLSpanElement } {
    const row = document.createElement('div');
    row.className = 'settings__row';

    const label = document.createElement('label');
    label.className = 'settings__row-label';
    label.textContent = args.label;
    row.appendChild(label);

    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'settings__range';
    range.min = String(args.min);
    range.max = String(args.max);
    range.step = String(args.step);
    range.value = String(args.get());
    label.htmlFor = '';
    range.addEventListener('input', () => {
      const v = parseFloat(range.value);
      args.set(v);
      valueEl.textContent = args.format(v);
    });
    row.appendChild(range);

    const valueEl = document.createElement('span');
    valueEl.className = 'settings__row-value';
    valueEl.textContent = args.format(args.get());
    row.appendChild(valueEl);

    return { row, range, valueEl };
  }

  // Resync sliders to current store values — needed when show() reopens the
  // panel after another surface (debug overlay, CLI, etc.) mutated a setting.
  private refresh(): void {
    if (this.masterRange !== null && this.masterValueEl !== null) {
      const v = this.opts.audio.getMasterVolume();
      this.masterRange.value = String(v);
      this.masterValueEl.textContent = `${Math.round(v * 100)}%`;
    }
    if (this.sfxRange !== null && this.sfxValueEl !== null) {
      const v = this.opts.audio.getSfxVolume();
      this.sfxRange.value = String(v);
      this.sfxValueEl.textContent = `${Math.round(v * 100)}%`;
    }
    if (this.musicRange !== null && this.musicValueEl !== null) {
      const v = this.opts.audio.getMusicVolume();
      this.musicRange.value = String(v);
      this.musicValueEl.textContent = `${Math.round(v * 100)}%`;
    }
    if (this.voiceRange !== null && this.voiceValueEl !== null) {
      const v = this.opts.audio.getVoiceVolume();
      this.voiceRange.value = String(v);
      this.voiceValueEl.textContent = `${Math.round(v * 100)}%`;
    }
    if (this.scrollRange !== null && this.scrollValueEl !== null) {
      const v = this.opts.settings.get().scrollSpeedPxPerSec;
      this.scrollRange.value = String(v);
      this.scrollValueEl.textContent = `${Math.round(v)} px/s`;
    }
    if (this.analyticsCheckbox !== null) {
      this.analyticsCheckbox.checked = this.opts.settings.get().analyticsEnabled;
    }
    this.applySelectedDifficulty(this.opts.settings.get().botDifficulty);
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(PANEL_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = PANEL_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${PANEL_ID} {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 60;
  background: rgba(2,4,6,0.78);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
}
/* V2 Tempo polish: matches main-menu Commander card — radial dual-glow,
 * scanline overlay, hex clip-path, Orbitron headings. */
.settings__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 40px;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.14), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.10), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.96), rgba(5, 9, 17, 0.98));
  border: 1px solid rgba(174, 240, 255, 0.28);
  border-radius: 0;
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  min-width: 460px;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 18px 50px rgba(0,0,0,0.7);
}
.settings__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 22px;
  opacity: 0.40;
  pointer-events: none;
}
.settings__card > * { position: relative; z-index: 1; }
.settings__title {
  margin: 0;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 18px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #aef0ff;
  text-align: center;
  text-shadow: 0 0 12px rgba(110,220,255,0.45);
}
.settings__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid rgba(174,240,255,0.18);
  padding-top: 12px;
}
.settings__section-heading {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: #6fdcff;
}
.settings__row {
  display: grid;
  grid-template-columns: 110px 1fr 70px;
  align-items: center;
  gap: 12px;
}
.settings__row-label {
  font-size: 12px;
  color: #cfd6dd;
}
.settings__range {
  width: 100%;
  accent-color: #f5e76e;
}
.settings__row-value {
  font-size: 11px;
  color: #b8c0c8;
  text-align: right;
}
.settings__help {
  font-size: 10px;
  color: #8c95a0;
  margin-top: 4px;
}
.settings__diff-group {
  display: flex;
  gap: 8px;
}
.settings__diff-btn {
  flex: 1;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  padding: 10px 14px;
  background: rgba(10, 19, 34, 0.55);
  color: #a3b8d2;
  border: 1px solid rgba(174,240,255,0.18);
  border-radius: 0;
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  cursor: pointer;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transition: color 150ms, border-color 150ms, box-shadow 150ms;
}
.settings__diff-btn:hover {
  color: #aef0ff;
  border-color: rgba(174,240,255,0.45);
  box-shadow: 0 0 12px rgba(30,167,255,0.20);
}
.settings__diff-btn--selected {
  color: #ffe680;
  border-color: rgba(255,230,128,0.55);
  background: linear-gradient(180deg, rgba(255,230,128,0.10), rgba(10,19,34,0.85));
  box-shadow: 0 0 14px rgba(255,230,128,0.28);
}
.settings__diff-btn:focus-visible {
  outline: 2px solid #aef0ff;
  outline-offset: 3px;
}
.settings__diff-blurb {
  font-size: 11px;
  color: #8c95a0;
  text-align: center;
  margin-top: 2px;
  min-height: 14px;
}
.settings__btn {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  padding: 12px 28px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.92));
  color: #cfd6dd;
  border: 1px solid rgba(174,240,255,0.28);
  border-radius: 0;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  cursor: pointer;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  align-self: center;
  margin-top: 4px;
  transition: color 150ms, box-shadow 150ms, transform 80ms;
  min-height: 44px;
}
.settings__btn--primary {
  background: linear-gradient(180deg, rgba(30, 167, 255, 0.20), rgba(255, 42, 42, 0.10));
  border-color: rgba(174,240,255,0.55);
  color: #eef4ff;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(174,240,255,0.45);
  box-shadow: 0 0 18px rgba(30,167,255,0.30);
}
.settings__btn:hover {
  color: #aef0ff;
  box-shadow: 0 0 18px rgba(30,167,255,0.30);
}
.settings__btn:active { transform: scale(0.98); }
.settings__btn:focus-visible {
  outline: 2px solid #aef0ff;
  outline-offset: 3px;
}
.settings__check-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.settings__checkbox {
  width: 16px;
  height: 16px;
  accent-color: #f5e76e;
  cursor: pointer;
  flex-shrink: 0;
}
.settings__check-label {
  font-size: 12px;
  color: #cfd6dd;
  cursor: pointer;
  user-select: none;
}

/* V3 mobile: shrink the modal, give sliders extra vertical space (touch
 * tracks need to be at least 32 px tall to be reliably draggable), and let
 * the panel scroll if the screen is very short (landscape on small phones). */
@media (max-width: 820px), (pointer: coarse) {
  .settings__card {
    min-width: 0;
    width: calc(100vw - 24px);
    max-width: 480px;
    padding: 18px 18px;
    gap: 12px;
    box-sizing: border-box;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }
  .settings__title { font-size: 16px; letter-spacing: 0.12em; }
  .settings__row { grid-template-columns: 100px 1fr 56px; gap: 8px; }
  .settings__row-label { font-size: 11px; }
  .settings__range { height: 32px; }
  .settings__row-value { font-size: 10px; }
  .settings__diff-btn { padding: 12px 8px; min-height: 44px; font-size: 11px; }
  .settings__btn { min-height: 48px; padding: 14px 28px; width: 100%; max-width: 280px; }
  /* Keyboard scroll-speed is irrelevant on touch — camera is driven by
   * single-finger drag or two-finger pinch/pan. Hide the Camera section
   * rather than confuse players with a slider that does nothing for them. */
  .settings__section--camera { display: none; }
}
`;
  document.head.appendChild(style);
}
